/**
 * Pyodide Service for running Python code in the browser
 * Replaces the Tauri Python plugin functionality
 */

import { PY_FULLCONTROL_WRAPPER } from './pythonWrapperSource';

// Global Pyodide instance
let pyodide: any = null;
let pyodideReadyPromise: Promise<any> | null = null;

/**
 * Initialize Pyodide with FullControl package
 */
export async function initializePyodide(): Promise<any> {
  if (pyodideReadyPromise) {
    return pyodideReadyPromise;
  }

  pyodideReadyPromise = (async () => {
    try {
  // Loading Pyodide
      
      // Load Pyodide from the CDN script we included in index.html
      if (typeof (window as any).loadPyodide === 'undefined') {
        throw new Error('Pyodide script not loaded. Make sure pyodide.js is included in index.html');
      }
      
      pyodide = await (window as any).loadPyodide();
      
  // Pyodide loaded
      
      // Install micropip first
      await pyodide.loadPackage('micropip');
      
  // Installing FullControl package
        // Install FullControl package
      await pyodide.runPythonAsync(`
        import micropip
        await micropip.install('fullcontrol')
        print("FullControl package installed successfully")
      `);
      
  // Pyodide initialization complete
      return pyodide;
    } catch (error) {
      console.error('Failed to initialize Pyodide:', error);
      throw error;
    }
  })();

  return pyodideReadyPromise;
}

/**
 * Get the initialized Pyodide instance
 */
export async function getPyodide(): Promise<any> {
  if (!pyodide) {
    await initializePyodide();
  }
  return pyodide;
}

/**
 * Execute Python code and return the result
 */
export async function runPythonCode(code: string): Promise<any> {
  try {
    const pyodideInstance = await getPyodide();
    
    // Automatically load packages from imports
    await pyodideInstance.loadPackagesFromImports(code);

    // Split code into lines to handle multi-line input
    const lines = code.trim().split('\n');
    
    if (lines.length === 0) {
      return {
        success: true,
        result: null,
        error: null
      };
    }

    // Try to determine if the last line is an expression
    const lastLine = lines[lines.length - 1].trim();
    
    // If there's only one line, try to eval it first
    if (lines.length === 1 && lastLine) {
      try {
        const result = pyodideInstance.runPython(lastLine);
        return {
          success: true,
          result: result,
          error: null
        };
      } catch {
        // If eval fails, try exec instead
        pyodideInstance.runPython(code);
        return {
          success: true,
          result: null,
          error: null
        };
      }
    }
    
    // Execute all lines except the last one
    if (lines.length > 1) {
      const precedingCode = lines.slice(0, -1).join('\n');
      pyodideInstance.runPython(precedingCode);
    }
    
    // Try to evaluate the last line as an expression
    if (lastLine) {
      try {
        // Check if the last line looks like an expression
        const hasStatementKeywords = ['=', 'def ', 'class ', 'if ', 'for ', 'while ', 
          'with ', 'try:', 'except:', 'finally:', 'import ', 'from ', 'global ', 
          'nonlocal ', 'del ', 'pass', 'break', 'continue', 'return', 'yield', 
          'raise', 'assert'].some(keyword => lastLine.includes(keyword));
        
        if (!hasStatementKeywords) {
          const result = pyodideInstance.runPython(lastLine);
          return {
            success: true,
            result: result,
            error: null
          };
        }
      } catch {
        // If eval failed, execute the last line
      }
      
      pyodideInstance.runPython(lastLine);
    }
    
    return {
      success: true,
      result: null,
      error: null
    };
    
  } catch (error: any) {
    return {
      success: false,
      result: null,
      error: error.message || String(error)
    };
  }
}

/**
 * Execute FullControl code and return visualization and G-code data
 */
export async function runFullControlCode(userCode: string): Promise<string> {
  try {
    const pyodideInstance = await getPyodide();
    
    // Indent the user code to fit inside the try block
    let indentedUserCode = userCode.split('\n').map(line => '    ' + line).join('\n');
    if (!indentedUserCode.trim()) {
      indentedUserCode = '    pass';
    }

    // Load the framework code using fetch from the public directory
    let frameworkCode = PY_FULLCONTROL_WRAPPER.replace('{userCode}', indentedUserCode);
    
    // Automatically load packages from imports in the user code
    // We check userCode specifically because frameworkCode imports are static/known
    await pyodideInstance.loadPackagesFromImports(userCode);

    // Execute the framework code
    // Capture stdout to debug
    pyodideInstance.runPython(`
import sys
from io import StringIO
sys.stdout = StringIO()
    `);
    
    const result = pyodideInstance.runPython(frameworkCode);
    
    const stdout = pyodideInstance.runPython("sys.stdout.getvalue()");
    console.log("Python stdout:", stdout);
    
    // Convert Python objects to JavaScript objects
    const jsResult = result.toJs({ dict_converter: Object.fromEntries });
    
    // Check for user code error captured in Python wrapper
    if (jsResult.error) {
      return JSON.stringify({
        success: false,
        result: null,
        error: jsResult.error
      });
    }

    // Check for plot generation error
    if (jsResult.plot_error) {
      return JSON.stringify({
        success: false,
        result: null,
        error: jsResult.plot_error
      });
    }

    // Check for G-code generation error
    if (jsResult.gcode_error) {
      return JSON.stringify({
        success: false,
        result: null,
        error: jsResult.gcode_error
      });
    }

    // console.log('FullControl execution result:', jsResult);

    return JSON.stringify({
      success: true,
      result: jsResult,
      error: null
    });
    
  } catch (error: any) {
    return JSON.stringify({
      success: false,
      result: null,
      error: error.message || String(error)
    });
  }
}

/**
 * Validate Python code syntax
 */
export async function validatePythonCode(code: string): Promise<any> {
  try {
    const pyodideInstance = await getPyodide();
    
    // Use Python's compile function to check syntax
    const validationCode = `
try:
    compile(${JSON.stringify(code)}, '<string>', 'exec')
    result = {"valid": True, "error": None}
except SyntaxError as e:
    result = {
        "valid": False,
        "error": {
            "message": str(e),
            "line": e.lineno,
            "column": e.offset
        }
    }
except Exception as e:
    result = {
        "valid": False,
        "error": {
            "message": str(e),
            "line": None,
            "column": None
        }
    }
result
`;
    
    const result = pyodideInstance.runPython(validationCode);
    return result.toJs({ dict_converter: Object.fromEntries });
    
  } catch (error: any) {
    return {
      valid: false,
      error: {
        message: error.message || String(error),
        line: null,
        column: null
      }
    };
  }
}

/**
 * Simple greeting function for testing
 * Equivalent to the old greet_python function
 */
export async function greetPython(message: string): Promise<string> {
  try {
    const pyodideInstance = await getPyodide();
    
    const greetCode = `
result = "${message}" + " from python"
result
`;
    
    const result = pyodideInstance.runPython(greetCode);
    return result;
    
  } catch (error: any) {
    return `Error: ${error.message || String(error)}`;
  }
}